import { useState, useEffect, useCallback } from "react";

import { X, ChevronDown } from "lucide-react";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import { useToast } from "../hooks/use-toast";
import { Skeleton } from "../components/ui/skeleton";
import { Button } from "./ui/button";
import coin from "../assets/images/placeholder/crypto-colour.png";

export default function TradePairForex({
  selectedAsset,
  onSelect,
  onChange,
  setParentRate,
  currencies,
  typeOfTrade,
  currentPairs,
  awaitingRateParent,
  onLoadingPair,
}) {
  const [tradePair, setTradePair] = useState([]);
  const [selectedPair, setSelectedPair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPair, setLoadingPair] = useState(false);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  // const {
  //   data: assetPairs,
  //   isLoadingPairs,
  //   pairError,
  // } = useFetchTradablePairs(selectedAsset?.assetId);

  useEffect(() => {
    if (!currentPairs) return;
    const fetchPairs = async () => {
      try {
        currentPairs.forEach((pair) => {
          if (pair.asset === "zar") {
            setSelectedPair(pair);

            if (onSelect) {
              onSelect(pair);
            }
          }
        });
        setTradePair(currentPairs);
        return;
      } catch (err) {
        toast({
          description: `${err}`,
          variant: "eighty",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchPairs();
  }, [currentPairs, onSelect, toast]);

  useEffect(() => {
    if (!tradePair || tradePair.length === 0) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [tradePair]);

  // useEffect(() => {
  //   if (!selectedPair) return;
  //   const fetchRate = async () => {
  //     try {
  //       const data = await getTradeRate(
  //         selectedAsset?.assetId,
  //         "buy",
  //         selectedPair?.pair
  //       );

  //     }
  //       setParentRate(data.data.data);
  //       return;
  //     } catch (error) {
  //       toast({
  //         description: `${error}`,
  //         variant: "eighty",
  //       });
  //     }
  //   };
  //   fetchRate();
  // }, [selectedPair]);

  const handleSelect = useCallback(
    (asset) => {
      setSelectedPair(asset);
      setLoadingPair(true);
      if (onSelect) {
        onSelect(asset);
      }
      if (onLoadingPair) {
        onLoadingPair(loadingPair);
      }
      setOpen(false);
    },
    [onSelect, onLoadingPair, loadingPair]
  );

  return (
    <>
      {selectedAsset && typeOfTrade === "Buy" && (
        <div className="grid grid-cols-4 gap-4 grid-rows-1 mb-3 py-8">
          <div className="space-y-2 col-span-1">
            <label className="text-gray-600 text-sm">Pay with</label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                {loading ? (
                  <div className="w-full flex items-center justify-between py-4  px-4 rounded-2xl border border-gray-300 bg-muted/50">
                    <div className="flex items-center gap-3 ">
                      <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />

                      <div className="flex flex-col gap-1">
                        <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                      </div>
                    </div>
                    <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
                  </div>
                ) : (
                  <Button
                    disabled={awaitingRateParent}
                    variant="outline"
                    className="w-full flex items-center justify-between py-8 rounded-2xl hover:border-brand-primary transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center">
                        {selectedPair ? (
                          <img
                            src={selectedPair?.assetIcon}
                            alt={selectedPair?.assetName}
                            width={32}
                            height={32}
                            className="text-white"
                          />
                        ) : (
                          <img
                            src={coin}
                            alt={"Default Asset"}
                            width={32}
                            height={32}
                            className="text-white"
                          />
                        )}
                      </div>
                      <div className="text-left">
                        <div className="font-semibold">
                          {selectedPair?.asset.toUpperCase() || "Select"}
                        </div>
                      </div>
                    </div>
                    <ChevronDown className="text-gray-400" />
                  </Button>
                )}
              </DialogTrigger>

              <DialogContent className="max-w-sm rounded-2xl p-6">
                <DialogClose asChild>
                  <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
                    <X size={18} />
                  </button>
                </DialogClose>

                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold text-gray-800">
                    Pay With
                  </DialogTitle>
                </DialogHeader>

                <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
                  {currentPairs &&
                    currentPairs?.map((asset) => {
                      return (
                        <button
                          key={asset.id}
                          onClick={() => {
                            handleSelect(asset);
                          }}
                          className="flex items-center gap-3 px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200"
                        >
                          <img
                            src={asset.assetIcon}
                            alt={asset.assetName}
                            className="w-6 h-6"
                          />
                          <div className="flex flex-col text-left">
                            <span>{asset.assetName}</span>
                            <span className="text-xs text-gray-500">
                              {asset.asset?.toUpperCase()}
                            </span>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-2 col-span-3">
            <label className="text-gray-600 text-sm">Available balance</label>
            <div className="p-5 rounded-2xl bg-gray-50 flex-1 flex items-center">
              <div className="text-xl font-semibold text-gray-600">
                {selectedPair?.availableAmountFormatted}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
